import CategoryOrSubcategoryPage, {
  CategoryOrSubcategoryPageProps,
} from '@c/CategoryOrSubcategoryPage';
import { DefaultLayout } from '@c/layouts';
import SEO from '@c/SEO';
import { BreadcrumbSchema } from '@c/StructuredData';
import { getBlackWeekProducts } from '@util/firestore/products/';
import { getProductQueryArgsFromContext } from '@util/getPropsFromConext';
import { GetServerSideProps } from 'next';

export default function BlackFridayCyberMondayPage({
  slugToTitle,
  keyword,
  results,
  pageInfo,
  initialTopFilters,
  initialSideFilters,
  categoryOptions,
  conditionOptions,
  brandOptions
}: CategoryOrSubcategoryPageProps) {
  // Client-side debug logging
  console.log('[Black Friday Client] Props received:', {
    resultsCount: results?.length || 0,
    pageInfo,
    slugToTitle
  });

  // Debug: Check if products are displaying
  console.log('[Black Friday Client] First 3 products:', results?.slice(0, 3).map(p => ({ title: p.title, sku: p.sku })));

  // Generate breadcrumb items for structured data
  const breadcrumbItems = [
    { name: 'Home', url: 'https://gearfocus.com/' },
    { name: 'Products', url: 'https://gearfocus.com/products' },
    { name: 'Black Friday & Cyber Monday', url: 'https://gearfocus.com/c/black-friday-cyber-monday' }
  ];

  return (
    <>
      <SEO
        title="Black Friday & Cyber Monday Gear Drops — Camera Equipment | GearFocus"
        description="Shop the best Black Friday and Cyber Monday deals on camera gear. Featuring top products, deals and steals, and most popular items. Verified sellers, 8.5% fees, GearFocus Protection."
        ogImage="/landings/creator-marketplace/creator-hero-banner.png"
        keywords={[
          'black friday camera deals',
          'cyber monday camera deals',
          'camera gear deals',
          'photography equipment sales',
          'filmmaking gear discounts',
          'used camera deals',
          'creator gear drops',
          'black week camera sales',
          'camera equipment black friday',
          'lens deals cyber monday'
        ]}
      />

      {/* Breadcrumb Schema */}
      <BreadcrumbSchema items={breadcrumbItems} />

      <CategoryOrSubcategoryPage
        categoryOptions={categoryOptions}
        slugToTitle="Black Friday & Cyber Monday Gear Drops"
        keyword={keyword}
        results={results}
        pageInfo={pageInfo}
        initialTopFilters={initialTopFilters}
        initialSideFilters={initialSideFilters}
        seller_id={null}
        brandOptions={brandOptions}
        conditionOptions={conditionOptions}
        curated_list={['deals-and-steals', 'popular']}
      />
    </>
  );
}

BlackFridayCyberMondayPage.getLayout = function getLayout(page: React.ReactElement) {
  return <DefaultLayout>{page}</DefaultLayout>;
};

export const getServerSideProps: GetServerSideProps<
  CategoryOrSubcategoryPageProps
> = async (context) => {
  context.res.setHeader(
    'Cache-Control',
    'public, max-age=0, s-maxage=300, stale-while-revalidate=600'
  );

  const args = await getProductQueryArgsFromContext(context);
  // Query both deals-and-steals AND popular curated lists
  // Algolia will use OR logic: (curated_lists:'deals-and-steals' OR curated_lists:'popular')
  // This combines ~45 deals + ~50 popular = ~70-88 unique products (after deduplication)
  args.curated_list = ['deals-and-steals', 'popular'];
  const { getProducts } = await import('@util/firestore/products/');

  const response = await getProducts(args);

  console.log('[Black Friday SSR] Products from combined lists:', response.results.length, 'Total available:', response.pageInfo.totalResults);

  return {
    props: {
      categoryOptions: args.categoryOptions,
      conditionOptions: args.conditionOptions,
      slugToTitle: 'Black Friday & Cyber Monday Gear Drops',
      brandOptions: args.brandOptions,
      keyword: args.keyword,
      is_expired: false,
      is_verified: false,
      results: response.results,
      pageInfo: response.pageInfo,
      seller_id: null,
      initialTopFilters: {
        limit: args.limit,
        sort: args.sort,
        view: (context.query.view as 'list') || 'grid',
      },
      initialSideFilters: {
        minPrice: args.minPrice,
        maxPrice: args.maxPrice,
        brand: args.brandFilters,
        condition: args.conditionFilters,
        category: null,
        deals: args.deals,
        useCases: args.useCases,
        additional: args.additionalFilters
      }
    },
  };
};
